// Formatting the app shares with nothing else. Kept apart from components so
// the money rules in particular live in exactly one place.

const MONTHS = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const MONTHS_SHORT = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu',
  'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const DAYS = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];

// Dates arrive as plain YYYY-MM-DD and must not be run through the Date
// constructor's timezone handling, or a trip can start a day early.
export function parseISODate(iso) {
  const [y, m, d] = String(iso).split('-').map(Number);
  return { y, m, d, dow: DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] };
}

export const dayLabel = (iso) => {
  const { m, d } = parseISODate(iso);
  return `${d} ${MONTHS[m - 1]}`;
};
export const dayShort = (iso) => {
  const { m, d } = parseISODate(iso);
  return `${d} ${MONTHS_SHORT[m - 1]}`;
};
export const dayOfWeek = (iso) => parseISODate(iso).dow;

export const rangeLabel = (from, to) => {
  if (!from) return '';
  if (!to) return dayLabel(from);
  const a = parseISODate(from), b = parseISODate(to);
  return a.m === b.m
    ? `${a.d}–${b.d} ${MONTHS[a.m - 1]}`
    : `${dayShort(from)} – ${dayShort(to)}`;
};

export const nextDate = (date) =>
  new Date(Date.parse(`${date}T00:00:00Z`) + 86400000).toISOString().slice(0, 10);

/* ── zones ───────────────────────────────────────────────────────────────── */
// A trip that crosses zones makes "what time is it" and "what day is it"
// questions with several answers, so every answer here is asked of a named zone
// rather than of the device — the phone's clock is only ever whatever the last
// airport told it, and the itinerary knows better.

const PART_OPTS = {
  hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
};
const utcParts = new Intl.DateTimeFormat('en-GB', { ...PART_OPTS, timeZone: 'UTC' });
const PARTS = new Map();

// A bad zone must degrade to UTC rather than blank the screen: zones arrive as
// free text from the importer, and one typo should not cost the whole day view.
function zoneParts(zone) {
  if (!zone) return utcParts;
  if (!PARTS.has(zone)) {
    let f = null;
    try { f = new Intl.DateTimeFormat('en-GB', { ...PART_OPTS, timeZone: zone }); } catch { f = null; }
    PARTS.set(zone, f);
  }
  return PARTS.get(zone) ?? utcParts;
}

function readParts(instant, zone) {
  const p = {};
  for (const { type, value } of zoneParts(zone).formatToParts(instant)) p[type] = value;
  return p;
}

// The calendar date an instant falls on, read in a zone.
export function zoneDate(instant, zone) {
  const p = readParts(instant, zone);
  return `${p.year}-${p.month}-${p.day}`;
}

// The clock an instant shows in a zone. Midnight reads 24 in some engines.
export function zoneTime(instant, zone) {
  const p = readParts(instant, zone);
  return `${String(Number(p.hour) % 24).padStart(2, '0')}:${p.minute}`;
}

// Zones are named for places, and the place is the useful half: Asia/Dubai is
// only ever shown to say "this clock is Dubai's, not the one you are reading".
// The database spells those places in English, and this app does not.
const ZONE_IT = {
  Rome: 'Roma', London: 'Londra', Paris: 'Parigi', Lisbon: 'Lisbona',
  Athens: 'Atene', Zurich: 'Zurigo', Brussels: 'Bruxelles', Vienna: 'Vienna',
  Copenhagen: 'Copenaghen', Moscow: 'Mosca', Cairo: 'Il Cairo',
  Beijing: 'Pechino', Seoul: 'Seul', Mexico_City: 'Città del Messico',
};
export const zoneLabel = (zone) => {
  if (!zone) return '';
  const city = String(zone).split('/').pop();
  return ZONE_IT[city] ?? city.replace(/_/g, ' ');
};

// Intl reads instants but will not build one, so the offset is recovered by
// formatting a guess and reading it back as if it were UTC. A second pass
// settles the case where the guess fell the far side of a DST change.
function zoneOffset(instant, zone) {
  const p = readParts(instant, zone);
  return Date.UTC(+p.year, +p.month - 1, +p.day,
    Number(p.hour) % 24, +p.minute, +p.second) - instant;
}

// The instant a calendar date opens in a zone.
export function zoneDayStart(date, zone) {
  const guess = Date.parse(`${date}T00:00:00Z`);
  const once = guess - zoneOffset(guess, zone);
  return guess - zoneOffset(once, zone);
}

// Where the traveller is standing right now.
//
// The itinerary already knows: every timed thing carries the zone it starts in
// and the zone it ends in, so the last boundary that has gone by names the
// place. A flight hands you over to its arrival zone on landing, not on
// departure — which is why, waiting between two flights in Dubai, today is
// Dubai's today: not the phone's, and not yet the next stop's.
export function travellerZone(events, now = Date.now()) {
  const marks = [];
  for (const e of events) {
    if (e.starts_at && e.timezone)
      marks.push({ t: Date.parse(e.starts_at), zone: e.timezone, leg: e.parent_id ? 1 : 0 });
    const endZone = e.end_timezone ?? e.timezone;
    if (e.ends_at && endZone)
      marks.push({ t: Date.parse(e.ends_at), zone: endZone, leg: e.parent_id ? 1 : 0 });
  }
  if (marks.length === 0)
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  // A leg is more precise than the journey wrapping it, so where the two share
  // an instant it is the leg that decides.
  marks.sort((a, b) => a.t - b.t || a.leg - b.leg);
  let zone = marks[0].zone;                  // before departure: where you leave from
  for (const m of marks) {
    if (m.t > now) break;
    zone = m.zone;
  }
  return zone;
}

/* ── durations ───────────────────────────────────────────────────────────── */

const hm = (min) => {
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h}h${m ? ` ${String(m).padStart(2, '0')}m` : ''}` : `${m} min`;
};

// "fra 1h 09m" — sitting at a gate, this is the only number that matters.
export function untilLabel(ms) {
  if (ms == null) return null;
  const min = Math.round(ms / 60000);
  if (min <= 0) return 'adesso';
  if (min < 60) return `fra ${min} min`;
  if (min < 1440) return `fra ${hm(min)}`;
  const d = Math.round(min / 1440);
  return `fra ${d} giorn${d === 1 ? 'o' : 'i'}`;
}

// How long something lasts, or how much of it is left.
export const spanLabel = (ms) =>
  ms == null || ms <= 0 ? null : hm(Math.round(ms / 60000));

// An event's clock time in its own timezone. A flight leaving Dubai at 10:25
// must read 10:25, not whatever that instant is in Rome.
export function localTime(event) {
  if (!event.starts_at) return null;
  try {
    return new Intl.DateTimeFormat('it-IT', {
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: event.timezone || 'UTC',
    }).format(new Date(event.starts_at));
  } catch {
    return String(event.starts_at).slice(11, 16);
  }
}

// The arrival zone, which is not always the departure zone. When a journey
// crosses zones and end_timezone is missing we cannot know the local arrival
// time, and showing the departure zone's clock would be actively misleading —
// so nothing is shown and the duration carries the information instead.
export function localEndTime(event) {
  if (!event.ends_at) return null;
  const zone = event.end_timezone
    ?? (event.kind === 'transport' && event.transport_mode !== 'walk' ? null : event.timezone);
  if (!zone) return null;
  try {
    return new Intl.DateTimeFormat('it-IT', {
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: zone,
    }).format(new Date(event.ends_at));
  } catch {
    return null;
  }
}

// True when the arrival lands on a different calendar day in its own zone —
// the "+1" a red-eye needs.
export function arrivesNextDay(event) {
  if (!event.ends_at || !event.starts_at) return false;
  const zone = event.end_timezone ?? event.timezone;
  if (!zone) return false;
  const day = (t) => new Intl.DateTimeFormat('en-CA',
    { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: zone }).format(new Date(t));
  try { return day(event.ends_at) !== day(event.starts_at); } catch { return false; }
}

const eur = new Intl.NumberFormat('it-IT',
  { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const eurCents = new Intl.NumberFormat('it-IT',
  { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
const plain = new Intl.NumberFormat('it-IT');

// Totals are expressed in the trip's home currency, which the schema has always
// allowed to be something other than the euro — and which this formatter used to
// ignore, printing "€" over a number that was pounds.
const HOME = { code: 'EUR' };
export const setHomeCurrency = (code) => { if (code) HOME.code = String(code).toUpperCase(); };

const fmt = (cents) => (HOME.code === 'EUR'
  ? (cents ? eurCents : eur)
  : new Intl.NumberFormat('it-IT', {
    style: 'currency', currency: HOME.code,
    minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0,
  }));

export const money = (n, opts = {}) =>
  n == null ? null : fmt(opts.cents).format(Number(n));
export const localAmount = (n, currency) =>
  n == null ? null : `${currency} ${plain.format(Number(n))}`;

export const BASIS_LABEL = {
  per_person: 'a persona', per_day: 'al giorno', per_night: 'a notte',
  per_vehicle: 'a veicolo', per_unit: 'a pezzo', per_group: '',
};

// How a total was reached — 'IDR 1.850.000 a persona × 4' rather than a bare
// figure. Showing the derivation is what makes a total auditable at a glance.
export function derivation(e) {
  if (!e.unit_amount || !e.currency_local) return null;
  const basis = BASIS_LABEL[e.price_basis] ?? '';
  const q = Number(e.quantity ?? 1);
  const mult = q > 1 ? ` × ${plain.format(q)}` : '';
  if (!basis && !mult) return null;
  return `${localAmount(e.unit_amount, e.currency_local)}${basis ? ' ' + basis : ''}${mult}`;
}

export const PAYMENT = {
  paid:         { label: 'pagato',         tone: 'ok' },
  prepaid:      { label: 'prepagato',      tone: 'ok' },
  deposit_paid: { label: 'acconto',        tone: 'attn' },
  due_on_site:  { label: 'in loco',        tone: 'attn' },
  to_settle:    { label: 'da saldare',     tone: 'attn' },
  unknown:      { label: 'da verificare',  tone: 'muted' },
};

export const KIND = {
  transport: { label: 'Viaggio',  icon: '✈' },
  lodging:   { label: 'Hotel',    icon: '⌂' },
  activity:  { label: 'Attività', icon: '◈' },
  meal:      { label: 'Tavola',   icon: '⊙' },
  note:      { label: 'Nota',     icon: 'ℹ' },
  admin:     { label: 'Pratica',  icon: '✎' },
};

export const CHANNEL = {
  getyourguide: 'GetYourGuide', viator: 'Viator', booking_com: 'Booking.com',
  airbnb: 'Airbnb', direct_web: 'sito ufficiale', whatsapp: 'WhatsApp',
  phone: 'telefono', email: 'email', in_person: 'di persona',
  walk_up: 'in loco', hotel_concierge: 'tramite hotel', platform: 'piattaforma',
};

export const telHref = (n) => `tel:${String(n).replace(/[^\d+]/g, '')}`;

// How you actually get in. Written on places since the first import, printed
// nowhere — so "walk_in" and "book on the website" looked identical to someone
// standing outside deciding whether to queue.
export const BOOKING_METHOD = {
  "phone": "si prenota al telefono",
  "email": "si prenota via email",
  "website": "si prenota sul sito",
  "platform": "si prenota sulla piattaforma",
  "whatsapp": "si prenota su WhatsApp",
  "walk_in": "non si prenota, si va",
  "not_needed": "nessuna prenotazione",
};

export const bookingLine = (place) => [
  BOOKING_METHOD[place?.booking_method] ?? null,
  place?.booking_note ?? null,
].filter(Boolean).join(' · ') || null;

// The age of a traveller, which is what age limits are actually checked against.
export function ageOn(birthdate, onDate) {
  if (!birthdate) return null;
  const [by, bm, bd] = String(birthdate).split('-').map(Number);
  const [ny, nm, nd] = String(onDate ?? new Date().toISOString().slice(0, 10)).split('-').map(Number);
  if (!by || !ny) return null;
  return ny - by - ((nm < bm || (nm === bm && nd < bd)) ? 1 : 0);
}

// Navigation is delegated to whatever maps app the phone prefers — it does
// driving directions far better than anything embedded here would.

// `lat && lon` read a coordinate of 0 as no coordinate at all, which is a real
// place on this trip: the equator runs through Indonesia.
export const placePoint = (place) => {
  const lat = Number(place?.lat), lon = Number(place?.lon);
  return Number.isFinite(lat) && Number.isFinite(lon) && (lat !== 0 || lon !== 0)
    ? { lat, lon } : null;
};

const placeQuery = (place) =>
  [place?.name, place?.address, place?.city].filter(Boolean).join(', ');

// What Google can be given to find this place, best evidence first. A place with
// none of these is not mappable, and the app should say so rather than offer a
// link that lands on the wrong continent.
const mapsTerm = (place) => {
  const p = placePoint(place);
  if (p) return `${p.lat},${p.lon}`;
  return placeQuery(place) || null;
};

// The same place, told to Google in words instead of numbers.
//
// A coordinate is exact and anonymous: it opens as a grey droplet in the middle
// of a city, and four of them on one route is a map you cannot read — which is
// which, and which one is the temple? The name is what you are actually looking
// for on the screen, so it leads, with the address and city behind it to keep
// "Warung Made" from resolving to a different Warung Made. Coordinates remain
// the fallback for a place that has a position and no name worth searching.
//
// "Worth searching" means the name comes with somewhere: an address, a city, a
// country, an id. A bare invented phrase — "Sewu, Bubrah e Lumbung", three
// temples the agent grouped under one entry — is not a thing Google can find,
// and a pin on the wrong island is worse than an unlabelled pin on the right
// one. Those keep their coordinates; the fix for them is a city or a place id.
const mapsLabel = (place) => {
  const named = placeQuery(place);
  const grounded = Boolean(place?.google_place_id || place?.address
    || place?.city || place?.country);
  if (named && grounded) return named;
  return mapsTerm(place) ?? named ?? null;
};

export const isMappable = (place) => Boolean(place && (place.maps_url || mapsTerm(place)));

// One place, shown rather than routed to. The first version asked for driving
// directions to a bare coordinate: it opened navigation from wherever the phone
// was, to an unnamed dot, with no hours and no name to check against. Directions
// are one tap away from here; the name is not recoverable the other way round.
export function mapsHref(place) {
  if (!place) return null;
  if (place.maps_url) return place.maps_url;
  const label = mapsLabel(place);
  if (!label) return null;
  // The place id is the exact establishment — this restaurant, not another of
  // the same name two streets away — so it is sent wherever it exists. The
  // query is the name either way, because that is what gets drawn on the pin.
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`
    + (place.google_place_id
      ? `&query_place_id=${encodeURIComponent(place.google_place_id)}` : '');
}

// Google's URL scheme has no form for a handful of loose pins: the only way to
// get more than one marker on one map is a route through them. So a day is drawn
// in the order it happens, which is the order it would be walked anyway, and a
// stop is drawn in the order its days run.
export const MAX_ROUTE_POINTS = 10;          // origin + 9 waypoints, Google's cap

// Deduplicated because the hotel is both where the day starts and, often, where
// dinner is.
export function mappablePlaces(places) {
  const out = [], seen = new Set();
  for (const pl of places ?? []) {
    const term = pl?.maps_url ?? mapsTerm(pl);
    if (!term || seen.has(term)) continue;
    seen.add(term);
    out.push(pl);
  }
  return out;
}

export function mapsRouteHref(places, { mode = 'driving' } = {}) {
  const list = mappablePlaces(places).slice(0, MAX_ROUTE_POINTS);
  if (list.length === 0) return null;
  if (list.length === 1) return mapsHref(list[0]);

  const terms = list.map((pl) => encodeURIComponent(mapsLabel(pl)));
  const ids = list.map((pl) => pl.google_place_id ?? null);
  const mid = list.slice(1, -1);
  const midIds = ids.slice(1, -1);
  const waypoints = terms.slice(1, -1).join('%7C');
  const enc = (v) => encodeURIComponent(v);

  // Google pairs waypoint_place_ids with waypoints one for one, so it is all or
  // nothing for the middle of the route; the two ends are independent and take
  // theirs whenever they have one. Where an id is missing the name still gets
  // geocoded and still gets drawn with its label.
  const allMidIds = mid.length > 0 && midIds.every(Boolean);

  return 'https://www.google.com/maps/dir/?api=1'
    + `&origin=${terms[0]}`
    + (ids[0] ? `&origin_place_id=${enc(ids[0])}` : '')
    + `&destination=${terms[terms.length - 1]}`
    + (ids[ids.length - 1] ? `&destination_place_id=${enc(ids[ids.length - 1])}` : '')
    + (waypoints ? `&waypoints=${waypoints}` : '')
    + (allMidIds ? `&waypoint_place_ids=${midIds.map(enc).join('%7C')}` : '')
    + `&travelmode=${mode}`;
}

export const fileSize = (bytes) =>
  bytes == null ? '' : bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

// Legs of a journey, earliest first, indexed by the journey they belong to.
// What deleting an entry actually costs, said before it happens. The clauses
// are not decoration: a boarding pass that survives its flight is findable in
// Documenti, and a diary entry that survives its plan is the record that you
// went — neither is obvious unless it is spelled out.
export function deletePrompt(event, legs = []) {
  const bits = [];
  if (legs.length) bits.push(`${legs.length} tratte`);
  if (event.documents?.length) {
    bits.push(event.documents.length === 1
      ? 'il documento resta in Documenti'
      : `i ${event.documents.length} documenti restano in Documenti`);
  }
  if (event.visit) bits.push('la voce nel diario resta');
  return `Eliminare «${event.title}»?${bits.length ? ` ${bits.join(' · ')}.` : ''}`;
}

export function legsByParent(events) {
  const by = new Map();
  for (const e of events) {
    if (!e.parent_id) continue;
    if (!by.has(e.parent_id)) by.set(e.parent_id, []);
    by.get(e.parent_id).push(e);
  }
  for (const list of by.values())
    list.sort((a, b) => String(a.starts_at ?? '').localeCompare(String(b.starts_at ?? '')));
  return by;
}

// Group events into the days of a trip, including days with nothing on them —
// an empty day is information, not a gap to hide.
// A visit that fulfils no event still happened, at a time, on a day — so it
// belongs on the day beside the things that were planned. Dressed as an entry
// the day renderers already understand: a start time and a zone is all the
// sorting and the printing need.
export const visitEntry = (v) => ({
  ...v,
  __visit: true,
  kind: 'activity',
  starts_at: v.visited_at,
  event_date: zoneDate(Date.parse(v.visited_at), v.timezone),
  sort_order: 0,
});

export function buildDays(trip, events, dayNotes, visits = []) {
  const map = new Map();
  const add = (date) => { if (!map.has(date)) map.set(date, { date, events: [], note: null }); };
  const legsBy = legsByParent(events);

  if (trip.start_date && trip.end_date) {
    const start = Date.parse(`${trip.start_date}T00:00:00Z`);
    const end = Date.parse(`${trip.end_date}T00:00:00Z`);
    for (let t = start; t <= end; t += 86400000)
      add(new Date(t).toISOString().slice(0, 10));
  }

  const unscheduled = [];
  for (const e of events) {
    if (e.parent_id) continue;                 // legs are shown inside their journey
    const legs = legsBy.get(e.id) ?? [];

    // A journey is a container: its legs hold the dates, and it belongs to every
    // day it is still under way on. Anchoring it to its departure alone is what
    // left the second morning of a two-day flight looking like it held nothing
    // but the hotel at the far end of it.
    const legDates = [...new Set(legs.map((l) => l.event_date).filter(Boolean))].sort();
    const dates = legDates.length ? legDates : (e.event_date ? [e.event_date] : []);

    // Pre-departure admin is real cost but not itinerary; it belongs to the
    // checklist, so it must not invent a day before the trip starts.
    if (dates.length === 0
        || (e.kind === 'admin' && trip.start_date && dates[0] < trip.start_date)) {
      unscheduled.push(e);
      continue;
    }

    for (const d of dates) {
      add(d);
      map.get(d).events.push({
        ...e,
        // Only the legs of this day, so a day shows the part of the journey that
        // actually happens on it.
        dayLegs: legs.filter((l) => (l.event_date ?? d) === d),
        startedOn: d === dates[0] ? null : dates[0],
        continuesOn: d === dates[dates.length - 1] ? null : dates[dates.length - 1],
      });
    }
  }
  // Only the ones standing on their own: a visit that fulfils an event is
  // already on the day as that event, wearing its «Fatto».
  for (const v of visits) {
    if (v.event_id || !v.visited_at) continue;
    const entry = visitEntry(v);
    add(entry.event_date);
    map.get(entry.event_date).events.push(entry);
  }

  for (const n of dayNotes) { add(n.date); map.get(n.date).note = n; }

  const days = [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
  // The clock decides, and sort_order only breaks ties.
  //
  // It was the other way round, and it put a 15:00 visit after a 17:30 one: the
  // original import numbered its entries 10, 20, 30, everything an agent has
  // added since arrived with the default 0, and the day was therefore ordered by
  // when each row happened to be written. A timeline that disagrees with its own
  // timestamps is not a timeline.
  //
  // sort_order still earns its place among entries with no clock at all — three
  // things "in mattinata", in the order they were meant to happen — and those
  // sort after everything timed, because a day is read from its first known hour.
  for (const d of days) {
    d.events.sort((a, b) => {
      const ta = localTime(a);
      const tb = localTime(b);
      if (ta && tb) return ta.localeCompare(tb) || (a.sort_order - b.sort_order);
      if (ta) return -1;
      if (tb) return 1;
      return a.sort_order - b.sort_order;
    });
  }
  return { days, unscheduled };
}

// The two ends of a journey: where it lifts off and where it lands, each as the
// calendar date and clock of its own place.
//
// A journey has one event_date and it is the departure, so the flight that lands
// in Kuala Lumpur on the 11th is filed under the 10th. Asking an event which stop
// it belongs to therefore gets a misleading answer — it belongs to two, at
// opposite ends, and its legs are what know that. The container often carries no
// times of its own, so the first and last leg answer for it.
export function journeyEnds(event, legs = []) {
  const first = legs[0] ?? event;
  const last = legs[legs.length - 1] ?? event;

  const departAt = first.starts_at ? Date.parse(first.starts_at) : null;
  const departZone = first.timezone ?? event.timezone ?? null;
  const arriveAt = (last.ends_at ?? event.ends_at) ? Date.parse(last.ends_at ?? event.ends_at) : null;
  const arriveZone = last.end_timezone ?? last.timezone ?? event.end_timezone ?? event.timezone ?? null;

  const departs = departAt != null ? zoneDate(departAt, departZone) : (event.event_date ?? null);
  return {
    departs,
    arrives: arriveAt != null ? zoneDate(arriveAt, arriveZone) : departs,
    departAt, arriveAt, departZone, arriveZone,
  };
}

/* ── today, step by step ─────────────────────────────────────────────────── */

// One day as a sequence of steps in the order they happen, waits included.
//
// A day here is a window of instants in the traveller's own zone rather than a
// date written on an event, because those are not the same thing in transit: the
// overnight leg that lands at 07:10 is part of this morning even though it was
// booked as yesterday's flight, and the connection three hours later is the next
// thing that happens — not a line buried inside yesterday's journey card.
export function buildToday(data, date, zone, now = Date.now()) {
  const { events, dayNotes = [], blocks = [], visits = [] } = data;
  const from = zoneDayStart(date, zone);
  const to = zoneDayStart(nextDate(date), zone);
  const overlaps = (a, b) => a < to && (b ?? a) >= from;
  const legsBy = legsByParent(events);
  const steps = [];

  for (const e of events) {
    if (e.parent_id) continue;
    const legs = legsBy.get(e.id) ?? [];

    if (legs.length > 0) {
      for (let i = 0; i < legs.length; i++) {
        const l = legs[i];
        const a = l.starts_at ? Date.parse(l.starts_at) : null;
        const b = l.ends_at ? Date.parse(l.ends_at) : null;
        if (a != null && overlaps(a, b))
          steps.push({
            type: 'leg', event: l, parent: e, at: a, until: b,
            zone: l.timezone, endZone: l.end_timezone ?? l.timezone,
          });

        // The wait between two legs is a step of the day too. It is where you
        // physically are, for hours, and left implicit it reads as a gap in the
        // plan rather than the layover it is.
        const nxt = legs[i + 1];
        if (!nxt || b == null || !nxt.starts_at) continue;
        const c = Date.parse(nxt.starts_at);
        if (c > b && overlaps(b, c))
          steps.push({
            type: 'wait', event: nxt, parent: e, at: b, until: c,
            zone: l.end_timezone ?? l.timezone, endZone: nxt.timezone,
            place: nxt.place ?? l.place ?? null, from: l,
          });
      }
      continue;
    }

    if (e.starts_at) {
      const a = Date.parse(e.starts_at);
      const b = e.ends_at ? Date.parse(e.ends_at) : null;
      // Something that merely continues through today — a hotel on its second
      // night — is not a step of today. A journey still under way is.
      const begins = a >= from && a < to;
      const ends = b != null && b >= from && b < to;
      if (begins || (e.kind === 'transport' && overlaps(a, b)))
        steps.push({
          type: 'event', event: e, at: a, until: b,
          zone: e.timezone, endZone: e.end_timezone ?? e.timezone, begins,
        });
      // Leaving is a step too. A 06:30 checkout to make a 09:40 flight is the
      // first thing that happens that morning, and the stay it belongs to
      // started three days earlier.
      else if (ends && e.kind === 'lodging')
        steps.push({
          type: 'end', event: e, at: b, until: null,
          zone: e.end_timezone ?? e.timezone, endZone: e.end_timezone ?? e.timezone,
        });
    } else if (e.event_date === date) {
      // No clock on it: it happens today, sometime. Ordered after what is timed.
      steps.push({ type: 'event', event: e, at: null, until: null, zone, begins: true });
    }
  }

  // Same rule as the day list: what happened and was never planned is a step of
  // today too, or the screen answers "what is happening" with only half of it.
  for (const v of visits) {
    if (v.event_id || !v.visited_at) continue;
    const at = Date.parse(v.visited_at);
    if (at >= from && at < to)
      steps.push({
        type: 'visit', visit: v, at, until: null, zone: v.timezone ?? zone,
        endZone: v.timezone ?? zone,
        // The renderers reach for step.event; a visit answers for itself.
        event: { id: v.id, kind: 'activity', title: v.title, place: v.place ?? null },
      });
  }

  steps.sort((a, b) =>
    (a.at == null) - (b.at == null)
    || (a.at ?? 0) - (b.at ?? 0)
    || (a.event.sort_order ?? 0) - (b.event.sort_order ?? 0));

  for (const s of steps) {
    s.done = s.type === 'visit' || (s.at != null && (s.until ?? s.at) <= now);
    s.live = s.type !== 'visit'
      && s.at != null && s.at <= now && (s.until ?? s.at) > now;
    s.key = `${s.type}:${s.event.id}`;
  }

  // The boarding passes are filed against the booking, not against each leg of
  // it. On the day itself they belong to the flight you are about to take —
  // hanging them off every leg turns one attachment into a row of duplicates.
  const byJourney = new Map();
  for (const s of steps) {
    if (s.type !== 'leg' || !s.parent?.documents?.length) continue;
    if (!byJourney.has(s.parent.id)) byJourney.set(s.parent.id, []);
    byJourney.get(s.parent.id).push(s);
  }
  for (const list of byJourney.values()) {
    const s = list.find((x) => !x.done) ?? list[list.length - 1];
    s.docs = [...(s.event.documents ?? []), ...s.parent.documents];
  }

  return {
    date, zone, from, to, steps,
    // The innermost thing in progress: a leg beats the journey containing it.
    current: steps.filter((s) => s.live).sort((a, b) =>
      ((a.until ?? 0) - (a.at ?? 0)) - ((b.until ?? 0) - (b.at ?? 0)))[0] ?? null,
    // Something already done cannot be the next thing to do, whatever its clock
    // says — a visit logged with a rounded-up time must not become the band.
    next: steps.find((s) => s.type !== 'visit' && s.at != null && s.at > now) ?? null,
    note: dayNotes.find((n) => n.date === date) ?? null,
    dayBlocks: blocks.filter((b) => b.parent_type === 'day' && b.parent_date === date),
  };
}

// A trip that crosses a border is two trips in one head, so the stop list says
// where the border is. Intl knows the Italian for every ISO code; the fallback
// is the code itself, which is still better than a heading that lies.
// One override, because the app is in Italian and so is the trip: Intl answers
// "Malaysia" for MY, which is correct and is not what anyone here calls it.
const COUNTRY_IT = { MY: 'Malesia' };

export function countryName(code) {
  if (!code) return null;
  const up = String(code).toUpperCase();
  if (COUNTRY_IT[up]) return COUNTRY_IT[up];
  try {
    return new Intl.DisplayNames(['it'], { type: 'region' }).of(up) ?? code;
  } catch { return code; }
}

export const stopForDate = (stops, date) =>
  stops.find((s) => s.arrive_date && s.depart_date
    && date >= s.arrive_date && date < s.depart_date) ?? null;

/* ── categories ──────────────────────────────────────────────────────────── */
//  What makes a stop scannable. Deliberately separate from `kind`: kind says
//  whether something is an activity or a meal, category says whether it is
//  sightseeing or a market. Open, so an agent inventing "diving" groups under
//  its own heading rather than being dropped into Other.

export const CATEGORY_LABEL = {
  monument: 'Monumenti e siti',
  museum: 'Musei',
  neighbourhood: 'Quartieri e passeggiate',
  nature: 'Natura e paesaggi',
  viewpoint: 'Panorami',
  experience: 'Esperienze',
  eating: 'Dove mangiare',
  nightlife: 'Sera',
  shopping: 'Mercati e shopping',
  wellness: 'Benessere',
  practical: 'Pratico',
};

// Order reflects how you read a city: what is worth going to, then what you
// wander, then what you do, then where you eat, then the admin.
const CATEGORY_ORDER = ['monument', 'museum', 'neighbourhood', 'nature', 'viewpoint',
  'experience', 'eating', 'nightlife', 'shopping', 'wellness', 'practical'];

// The first vocabulary put a temple and an afternoon wandering a historic centre
// under one heading. They are not the same activity: one is a site you go to,
// with an entrance and an opening time; the other is an area you walk through.
// Planning them differs, so they are separate categories.
//
// Older spellings still resolve, because data written before this change should
// not silently land in Altro.
//
// The aliases also carry the language problem. An agent writing an Italian trip
// writes Italian categories — museo, palazzo, sito storico, mercato — and each
// unrecognised one opened a heading of its own with a single card under it. A
// stop came out as eight sections of one thing each, which is a list wearing a
// costume. Anything that means the same thing lands in the same place now.
const CATEGORY_ALIAS = {
  // English, and older spellings this app itself used to write
  sightseeing: 'monument',
  walking: 'neighbourhood',
  attraction: 'monument',
  attractions: 'monument',
  landmark: 'monument',
  temple: 'monument',
  church: 'monument',
  mosque: 'monument',
  palace: 'monument',
  ruins: 'monument',
  historic: 'monument',
  'historic site': 'monument',
  market: 'shopping',
  beach: 'nature',
  park: 'nature',
  garden: 'nature',
  hike: 'nature',
  food: 'eating',
  restaurant: 'eating',
  dinner: 'eating',
  lunch: 'eating',
  breakfast: 'eating',
  cafe: 'eating',
  bar: 'nightlife',
  show: 'nightlife',
  spa: 'wellness',
  transport: 'practical',
  // Italian, which is what the agent actually writes for an Italian trip
  monumento: 'monument',
  monumenti: 'monument',
  museo: 'museum',
  musei: 'museum',
  palazzo: 'monument',
  castello: 'monument',
  tempio: 'monument',
  moschea: 'monument',
  chiesa: 'monument',
  'sito storico': 'monument',
  'siti storici': 'monument',
  storico: 'monument',
  quartiere: 'neighbourhood',
  quartieri: 'neighbourhood',
  passeggiata: 'neighbourhood',
  centro: 'neighbourhood',
  natura: 'nature',
  parco: 'nature',
  giardino: 'nature',
  spiaggia: 'nature',
  panorama: 'viewpoint',
  vista: 'viewpoint',
  esperienza: 'experience',
  esperienze: 'experience',
  attivita: 'experience',
  'attività': 'experience',
  corso: 'experience',
  mercato: 'shopping',
  mercati: 'shopping',
  negozi: 'shopping',
  cena: 'eating',
  pranzo: 'eating',
  colazione: 'eating',
  ristorante: 'eating',
  cibo: 'eating',
  cucina: 'eating',
  sera: 'nightlife',
  serata: 'nightlife',
  benessere: 'wellness',
  pratico: 'practical',
  trasporto: 'practical',
};

// A cuisine is not a category. «Giavanese», «Padang», «malese» name what is on
// the plate, and the question the traveller is asking of that card is still
// "where do we eat" — so anything that reads as a cuisine folds into eating
// rather than opening a heading nobody was looking for.
const CUISINE = /(ese|ana|iana|ano|ese|thai|sushi|halal|veg|street\s*food|seafood|bbq|grill)$/i;

export const normaliseCategory = (c) => {
  if (!c) return null;
  const raw = String(c).trim().toLowerCase();
  if (CATEGORY_ALIAS[raw]) return CATEGORY_ALIAS[raw];
  if (CATEGORY_ORDER.includes(raw)) return raw;
  if (CUISINE.test(raw)) return 'eating';
  return raw;
};

const FROM_PLACE_KIND = {
  restaurant: 'eating', attraction: 'monument', viewpoint: 'viewpoint',
  beach: 'nature', shop: 'shopping', airport: 'practical', station: 'practical',
};
const FROM_EVENT_KIND = { meal: 'eating', activity: 'experience', admin: 'practical' };

// An explicit category wins, then the place it happens at, then the shape of the
// thing itself. Something with no signal at all lands in Altro rather than
// vanishing.
export function categoryOf(x) {
  return normaliseCategory(x?.category)
    ?? normaliseCategory(x?.place?.category)
    ?? FROM_PLACE_KIND[x?.place?.kind]
    ?? FROM_EVENT_KIND[x?.kind]
    ?? (x?.shortlist_kind === 'restaurant' ? 'eating' : null)
    ?? 'other';
}

export const categoryLabel = (c) =>
  CATEGORY_LABEL[c] ?? (c === 'other' ? 'Altro' : c.charAt(0).toUpperCase() + c.slice(1));

export function groupByCategory(items) {
  const groups = new Map();
  for (const it of items) {
    const c = categoryOf(it);
    if (!groups.has(c)) groups.set(c, []);
    groups.get(c).push(it);
  }
  const known = CATEGORY_ORDER.filter((c) => groups.has(c));
  const unknown = [...groups.keys()].filter((c) => !CATEGORY_ORDER.includes(c)).sort();
  return [...known, ...unknown].map((c) => [c, groups.get(c)]);
}

// A wall-clock time in a named zone, as an instant. Needed because the app
// collects "19:30 in Kuala Lumpur" and the database stores a timestamp: guessing
// the offset would put dinner an hour out twice a year.
export function isoInZone(dateStr, timeStr, zone) {
  if (!dateStr || !timeStr) return null;
  const asIfUTC = Date.parse(`${dateStr}T${timeStr}:00Z`);
  if (Number.isNaN(asIfUTC)) return null;
  if (!zone) return new Date(asIfUTC).toISOString();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, hour12: false, year: 'numeric', month: '2-digit',
      day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(new Date(asIfUTC))
      .reduce((a, p) => (a[p.type] = p.value, a), {});
    const back = Date.UTC(+parts.year, +parts.month - 1, +parts.day,
      +parts.hour % 24, +parts.minute, +parts.second);
    return new Date(asIfUTC - (back - asIfUTC)).toISOString();
  } catch {
    return new Date(asIfUTC).toISOString();
  }
}

// The maps a stop is worth having: the whole stay, one per day that has
// somewhere to be, and the candidates that are still only proposals — which is
// where a map earns its keep, because "are these four temples near each other"
// is not a question a list can answer.
//
// Every one of them is the same question asked of a narrower set, so they are
// built here once rather than assembled differently in two screens.
export function stopMaps({ stop, events = [], options = [], lodging = [] }) {
  // A day starts where you slept, so the route does too. Failing a hotel, the
  // stop's own coordinates stand in for "the city".
  const home = mappablePlaces(lodging.map((e) => e.place).filter(Boolean))[0]
    ?? (placePoint(stop) ? { name: stop.name, lat: stop.lat, lon: stop.lon } : null);

  const byDay = new Map();
  for (const e of events) {
    if (!e.place || !e.event_date) continue;
    if (!byDay.has(e.event_date)) byDay.set(e.event_date, []);
    byDay.get(e.event_date).push(e.place);
  }

  const optionPlaces = mappablePlaces(options.map((i) => i.place).filter(Boolean));
  const maps = [];

  const everything = mappablePlaces([home, ...events.map((e) => e.place), ...optionPlaces]);
  if (everything.length > 1)
    maps.push({ key: 'all', label: 'Tutta la tappa', places: everything });

  for (const date of [...byDay.keys()].sort()) {
    const day = mappablePlaces(byDay.get(date));
    if (day.length === 0) continue;
    maps.push({ key: date, date, label: dayShort(date), places: mappablePlaces([home, ...day]) });
  }

  if (optionPlaces.length > 0)
    maps.push({ key: 'options', label: 'Da valutare', places: mappablePlaces([home, ...optionPlaces]) });

  return maps;
}

// Every date of a stay, so a choice is made from the days that exist rather than
// typed into a free-text field.
export function daysOfStop(stop) {
  if (!stop?.arrive_date) return [];
  const out = [];
  const end = Date.parse(`${stop.depart_date ?? stop.arrive_date}T00:00:00Z`);
  for (let t = Date.parse(`${stop.arrive_date}T00:00:00Z`); t <= end; t += 86400000)
    out.push(new Date(t).toISOString().slice(0, 10));
  return out;
}
